import { Container, ContainerArgs, Label, NumericInput, VectorInput } from '@playcanvas/pcui';
import { Quat, Vec3 } from 'playcanvas';

import { Events } from '../events';
import { localize } from './localization';
import { Pivot } from '../pivot';

const v = new Vec3();

class Transform extends Container {
    constructor(events: Events, args: ContainerArgs = {}) {
        args = {
            ...args,
            id: 'transform'
        };

        super(args);

        // position
        const position = new Container({
            class: 'transform-row'
        });

        const positionLabel = new Label({
            class: 'transform-label',
            text: localize('panel.scene-manager.transform.position')
        });

        const positionVector = new VectorInput({
            class: 'transform-expand',
            precision: 3,
            dimensions: 3,
            placeholder: ['X', 'Y', 'Z'],
            value: [0, 0, 0],
            enabled: false
        });

        position.append(positionLabel);
        position.append(positionVector);

        // rotation
        const rotation = new Container({
            class: 'transform-row'
        });

        const rotationLabel = new Label({
            class: 'transform-label',
            text: localize('panel.scene-manager.transform.rotation')
        });

        const rotationVector = new VectorInput({
            class: 'transform-expand',
            precision: 2,
            dimensions: 3,
            placeholder: ['X', 'Y', 'Z'],
            value: [0, 0, 0],
            enabled: false
        });

        rotation.append(rotationLabel);
        rotation.append(rotationVector);

        // scale
        const scale = new Container({
            class: 'transform-row'
        });

        const scaleLabel = new Label({
            class: 'transform-label',
            text: localize('panel.scene-manager.transform.scale')
        });

        const scaleInput = new NumericInput({
            class: 'transform-expand',
            precision: 3,
            value: 1,
            min: 0.001,
            max: 10000,
            enabled: false
        });

        scale.append(scaleLabel);
        scale.append(scaleInput);

        this.append(position);
        this.append(rotation);
        this.append(scale);

        const toArray = (v: Vec3) => {
            return [v.x, v.y, v.z];
        };

        let uiUpdating = false;
        let mouseUpdating = false;

        // briefly flash an input to signal its value just settled to a new
        // value (undo, focus jump, gizmo release) rather than silently snapping
        const flash = (input: { dom?: HTMLElement }) => {
            const el = input?.dom;
            if (!el) return;
            el.classList.add('transform-value-flash');
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    el.classList.remove('transform-value-flash');
                });
            });
        };

        const flashChanged = (inputs: NumericInput[], prev: number[], next: number[], epsilon = 1e-4) => {
            inputs.forEach((input, i) => {
                if (Math.abs((prev[i] ?? next[i]) - next[i]) > epsilon) {
                    flash(input as unknown as { dom?: HTMLElement });
                }
            });
        };

        // ease a set of numeric inputs from their current values to target
        // values over a short duration, rather than snapping instantly
        const tweenValues = (inputs: NumericInput[], from: number[], to: number[], duration = 140) => {
            const start = performance.now();
            const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

            const step = (now: number) => {
                const t = Math.min(1, (now - start) / duration);
                const e = easeOutCubic(t);
                inputs.forEach((input, i) => {
                    input.value = from[i] + (to[i] - from[i]) * e;
                });
                if (t < 1) {
                    requestAnimationFrame(step);
                }
            };
            requestAnimationFrame(step);
        };

        // update UI with pivot. tween/flash only used for "settle" updates
        // (selection change, undo, focus jump) - not during a live gizmo drag,
        // which already updates every frame and shouldn't be fought
        const updateUI = (pivot: Pivot, animate = false) => {
            uiUpdating = true;
            const transform = pivot.transform;
            transform.rotation.getEulerAngles(v);

            const nextPosition = toArray(transform.position);
            const nextRotation = toArray(v);
            const nextScale = transform.scale.x;

            if (animate) {
                const prevPosition = positionVector.value as number[];
                const prevRotation = rotationVector.value as number[];
                const prevScale = scaleInput.value as number;

                flashChanged(positionVector.inputs as NumericInput[], prevPosition, nextPosition);
                flashChanged(rotationVector.inputs as NumericInput[], prevRotation, nextRotation);
                flashChanged([scaleInput], [prevScale], [nextScale]);

                tweenValues(positionVector.inputs as NumericInput[], prevPosition, nextPosition);
                tweenValues(rotationVector.inputs as NumericInput[], prevRotation, nextRotation);
                tweenValues([scaleInput], [prevScale], [nextScale]);
            } else {
                positionVector.value = nextPosition;
                rotationVector.value = nextRotation;
                scaleInput.value = nextScale;
            }
            uiUpdating = false;
        };

        // update pivot with UI
        const updatePivot = (pivot: Pivot) => {
            const p = positionVector.value;
            const r = rotationVector.value;
            const q = new Quat().setFromEulerAngles(r[0], r[1], r[2]);
            const s = scaleInput.value;

            if (q.w < 0) {
                q.mulScalar(-1);
            }

            pivot.moveTRS(new Vec3(p[0], p[1], p[2]), q, new Vec3(s, s, s));
        };

        // handle a change in the UI state
        const change = () => {
            if (!uiUpdating) {
                const pivot = events.invoke('pivot') as Pivot;
                if (mouseUpdating) {
                    updatePivot(pivot);
                } else {
                    pivot.start();
                    updatePivot(pivot);
                    pivot.end();
                }
            }
        };

        const mousedown = () => {
            mouseUpdating = true;
            const pivot = events.invoke('pivot') as Pivot;
            pivot.start();
        };

        const mouseup = () => {
            const pivot = events.invoke('pivot') as Pivot;
            updatePivot(pivot);
            mouseUpdating = false;
            pivot.end();
        };

        [positionVector.inputs, rotationVector.inputs, scaleInput].flat().forEach((input) => {
            input.on('change', change);
            input.on('slider:mousedown', mousedown);
            input.on('slider:mouseup', mouseup);
        });

        // toggle ui availability based on selection
        events.on('selection.changed', (selection) => {
            positionVector.enabled = rotationVector.enabled = scaleInput.enabled = !!selection;
        });

        events.on('pivot.placed', (pivot: Pivot) => {
            updateUI(pivot, true);
        });

        events.on('pivot.moved', (pivot: Pivot) => {
            if (!mouseUpdating) {
                updateUI(pivot);
            }
        });

        events.on('pivot.ended', (pivot: Pivot) => {
            updateUI(pivot, true);
        });
    }
}

export { Transform };
